/**
 * features/leads/LeadsManager.tsx
 * Phase 6 — Leads. Website enquiries land here as a simple pipeline:
 * new → contacted → quoted → won/lost. Open a lead to change status,
 * add notes, convert it into a client, or delete it.
 */
import { useMemo, useState } from 'react';
import { Sparkles, Search, X, UserPlus, Trash2, Check, Mail, Phone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLeads } from './useLeads';
import { setLeadStatus, updateLeadNotes, convertToClient, deleteLead } from './leadsWrite';
import { glassSurface } from '@/components/ui/glass';
import { Button, Card, Input, Badge, Spinner, Textarea } from '@/components/ui/primitives';
import type { Lead, LeadStatus } from '@/types';

const STATUSES: LeadStatus[] = ['new', 'contacted', 'quoted', 'won', 'lost'];
const TONE: Record<LeadStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  new: 'brand',
  contacted: 'warning',
  quoted: 'neutral',
  won: 'success',
  lost: 'danger',
};
function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

function LeadDetail({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const { user } = useAuth();
  const actor = user?.email ?? user?.uid ?? 'unknown';
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function convert() {
    if (!window.confirm('Convert this lead into a client?')) return;
    setBusy(true);
    try {
      await updateLeadNotes(lead.id, notes);
      await convertToClient(lead, actor);
      setMsg('Converted to a client ✓');
      setTimeout(onClose, 900);
    } catch {
      setMsg('Could not convert.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-10 w-full max-w-lg">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <Sparkles size={18} />
              <h2 className="text-base font-semibold text-white">{lead.name}</h2>
              {lead.convertedClientId && (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">client</span>
              )}
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {lead.company && <div className="text-sm font-medium text-primary dark:text-gray-100">{lead.company}</div>}
          <div className="flex flex-wrap gap-3 text-sm text-primaryLight dark:text-gray-300">
            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-brand">
              <Mail size={14} /> {lead.email}
            </a>
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-brand">
                <Phone size={14} /> {lead.phone}
              </a>
            )}
          </div>
          {lead.message && (
            <div className="rounded-xl bg-black/[0.03] px-3 py-2 text-sm text-primary dark:bg-white/5 dark:text-gray-200">
              {lead.message}
            </div>
          )}

          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => void setLeadStatus(lead.id, s)}
                  className={`rounded-full px-3 py-1 text-sm font-semibold capitalize transition ${
                    lead.status === s ? 'bg-brand text-white' : 'bg-black/5 text-primaryLight hover:bg-black/10 dark:bg-white/5 dark:text-gray-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-primaryLight dark:text-gray-400">Notes</div>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => void updateLeadNotes(lead.id, notes)} />
          </div>

          {msg && <p className="text-sm font-semibold text-green-600 dark:text-green-400">{msg}</p>}

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              icon={<Trash2 size={16} />}
              onClick={() => {
                if (window.confirm('Delete this lead?')) {
                  void deleteLead(lead.id);
                  onClose();
                }
              }}
            >
              Delete
            </Button>
            <Button
              icon={lead.convertedClientId ? <Check size={16} /> : <UserPlus size={16} />}
              onClick={convert}
              loading={busy}
              disabled={!!lead.convertedClientId}
            >
              {lead.convertedClientId ? 'Converted' : 'Convert to client'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LeadsManager() {
  const { leads, loading } = useLeads();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | LeadStatus>('all');
  const [open, setOpen] = useState<Lead | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: leads.length };
    STATUSES.forEach((s) => (c[s] = leads.filter((l) => l.status === s).length));
    return c;
  }, [leads]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads
      .filter((l) => (filter === 'all' ? true : l.status === filter))
      .filter((l) => !q || l.name.toLowerCase().includes(q) || (l.company ?? '').toLowerCase().includes(q) || l.email.toLowerCase().includes(q));
  }, [leads, filter, search]);

  return (
    <div className="space-y-5">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, or email…" className="pl-9" />
      </div>

      <div className="flex flex-wrap gap-2">
        {(['all', ...STATUSES] as Array<'all' | LeadStatus>).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1 text-sm font-semibold capitalize transition ${
              filter === s ? 'bg-brand text-white' : 'bg-black/5 text-primaryLight hover:bg-black/10 dark:bg-white/5 dark:text-gray-300'
            }`}
          >
            {s} {counts[s] ? `(${counts[s]})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner label="Loading leads…" />
      ) : visible.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <Sparkles size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search || filter !== 'all' ? 'No leads match.' : 'No enquiries yet. Share your enquiry link to start collecting leads.'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setOpen(l)}
                  className="cursor-pointer border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3 font-semibold text-primary dark:text-gray-100">{l.name}</td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{l.company ?? '—'}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{l.email}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(l.createdAt)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={TONE[l.status]}>{l.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {open && <LeadDetail lead={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
